import { readFileSync, readdirSync, existsSync } from "node:fs";
import { resolve, dirname, basename } from "node:path";
import { globSync } from "glob";
import { getGitDiff } from "../git.js";
import type { SyncTarget } from "../types.js";

export type BriefFormat = "markdown" | "xml";

/** Build a single combined prompt covering all targets */
export async function buildCombinedBrief(
  targets: SyncTarget[],
  projectRoot: string,
  format: BriefFormat = "markdown"
): Promise<string> {
  const sections: string[] = [];

  for (const target of targets) {
    sections.push(await buildFileSection(target, projectRoot, format));
  }

  if (format === "xml") {
    const docs = sections
      .map((s, i) => `  <document index="${i + 1}">\n${s}\n  </document>`)
      .join("\n\n");
    return `<documents>\n${docs}\n</documents>\n\nFix all drift issues above. Only change what's broken. Use filesystem context to resolve missing paths. Apply fixes silently.`;
  }

  return `Fix the drift issues in these scaffold files. Rules:
1. Only change what's broken — do not rewrite correct sections.
2. When a path no longer exists, find the current path from the filesystem context and update the reference.
3. Apply fixes silently — do not explain what you changed.

${sections.map((s, i) => `━━━ File ${i + 1}/${sections.length} ━━━\n\n${s}`).join("\n\n")}`;
}

/** Build a targeted prompt for AI to fix a single file */
export async function buildSyncBrief(
  target: SyncTarget,
  projectRoot: string,
  format: BriefFormat = "markdown"
): Promise<string> {
  const section = await buildFileSection(target, projectRoot, format);

  if (format === "xml") {
    return `<documents>\n  <document index="1">\n${section}\n  </document>\n</documents>\n\nFix the drift issues above. Only change what's broken. Use filesystem context to resolve missing paths. Apply fixes silently.`;
  }

  return `Fix the drift issues in this scaffold file. Only change what's broken. When a path no longer exists, use the filesystem context to find the current path. Apply fixes silently.

${section}`;
}

/** Build the content section for a single target (no wrapper instructions) */
async function buildFileSection(
  target: SyncTarget,
  projectRoot: string,
  format: BriefFormat = "markdown"
): Promise<string> {
  const filePath = resolve(projectRoot, target.file);
  let fileContent: string;
  const MAX_FILE_CHARS = 8_000;
  try {
    const raw = readFileSync(filePath, "utf-8");
    // Strip frontmatter — it's system metadata, not actionable by Claude
    const stripped = raw.replace(/^---\n[\s\S]*?\n---\n*/, "");
    fileContent = stripped.length > MAX_FILE_CHARS
      ? stripped.slice(0, MAX_FILE_CHARS) + `\n\n... (truncated — ${stripped.length - MAX_FILE_CHARS} more chars)`
      : stripped;
  } catch (err) {
    fileContent = `(file could not be read: ${(err as Error).message})`;
  }

  const issueList = target.issues
    .map((i) => `- [${i.severity}] ${i.code}: ${i.message}`)
    .join("\n");

  // Get git diff for paths referenced by this file's claims
  const claimedPaths = target.issues
    .filter((i) => i.claim?.kind === "path")
    .map((i) => i.claim!.value);

  const diff = claimedPaths.length
    ? await getGitDiff(claimedPaths, projectRoot)
    : target.gitDiff ?? "";

  const MAX_DIFF_CHARS = 3_000;
  const diffTruncated = diff.length > MAX_DIFF_CHARS
    ? diff.slice(0, MAX_DIFF_CHARS) + `\n\n... (truncated — ${diff.length - MAX_DIFF_CHARS} more chars)`
    : diff;

  // For MISSING_PATH issues, find what actually exists nearby
  const fileContext = buildFileContext(target, projectRoot);

  if (format === "xml") {
    const contextPart = fileContext
      ? `\n    <filesystem_context>\n${fileContext}\n    </filesystem_context>` : "";
    const diffPart = diffTruncated
      ? `\n    <git_diff><![CDATA[\n${diffTruncated}\n    ]]></git_diff>` : "";
    return `    <source>${target.file}</source>
    <issues><![CDATA[
${issueList}
    ]]></issues>
    <current_content><![CDATA[
${fileContent}
    ]]></current_content>${contextPart}${diffPart}`;
  }

  let section = `**File:** ${target.file}

**Issues found:**
${issueList}

**Current file content:**
\`\`\`markdown
${fileContent}
\`\`\``;

  if (fileContext) {
    section += `

**Filesystem context (what actually exists):**
${fileContext}`;
  }

  if (diffTruncated) {
    section += `

**Recent git changes in referenced paths:**
\`\`\`diff
${diffTruncated}
\`\`\``;
  }

  return section;
}

/** For missing path issues, list actual files in the relevant directories */
function buildFileContext(
  target: SyncTarget,
  projectRoot: string
): string | null {
  const missingPaths = target.issues
    .filter((i) => i.code === "MISSING_PATH" && i.claim?.kind === "path")
    .map((i) => i.claim!.value);

  if (missingPaths.length === 0) return null;

  const sections: string[] = [];
  const listedDirs = new Set<string>();

  // Collect unique extensions up front to avoid repeated globs for the same extension
  const extToSearch = new Set<string>();
  for (const missing of missingPaths) {
    const name = basename(missing);
    const ext = name.includes(".") ? name.split(".").pop() : null;
    if (ext) extToSearch.add(ext);
  }

  // Run one glob per unique extension
  const extMatches = new Map<string, string[]>();
  for (const ext of extToSearch) {
    const matches = globSync(`**/*.${ext}`, {
      cwd: projectRoot,
      ignore: ["node_modules/**", ".cai/**", ".context-condensing/**", "dist/**", ".git/**"],
      maxDepth: 5,
    });
    if (matches.length > 0) extMatches.set(ext, matches);
  }

  for (const missing of missingPaths) {
    // Get the directory the missing file was expected in
    const dir = missing.includes("/") ? dirname(missing) : ".";
    const dirKey = dir === "." ? "root" : dir;

    // List the directory contents (skip if already listed)
    if (!listedDirs.has(dirKey)) {
      listedDirs.add(dirKey);

      const absDir = resolve(projectRoot, dir);
      if (existsSync(absDir)) {
        try {
          const files = readdirSync(absDir)
            .filter((f) => !f.startsWith("."))
            .sort()
            .slice(0, 30);
          const suffix = files.length === 30 ? " …" : "";
          if (files.length > 0) {
            sections.push(`\`${dir}/\` contains: ${files.join(", ")}${suffix}`);
          }
        } catch {
          // skip unreadable dirs
        }
      }
    }

    // Fuzzy search: show pre-computed matches for this extension
    const name = basename(missing);
    const ext = name.includes(".") ? name.split(".").pop() : null;
    if (ext && extMatches.has(ext)) {
      const matches = extMatches.get(ext)!;
      const shown = matches.slice(0, 15);
      const suffix = matches.length > 15 ? ` … and ${matches.length - 15} more` : "";
      sections.push(`All \`.${ext}\` files in project: ${shown.join(", ")}${suffix}`);
      extMatches.delete(ext); // show each extension only once
    }
  }

  return sections.length > 0 ? sections.join("\n") : null;
}
